import { useState } from 'react'
import {
	ArrowDownToLine,
	ArrowUpFromLine,
	Trophy,
	Gift,
	ChevronDown,
	RefreshCw,
} from 'lucide-react'

import { cn } from '@/common/lib/utils'
import {
	Collapsible,
	CollapsibleContent,
	CollapsibleTrigger,
} from '@/common/ui/collapsible'
import { Skeleton } from '@/common/ui/skeleton'
import { Button } from '@/common/ui/button'

import useQueryGetPlayerCumulative from '@/modules/manage-player/api/hooks/useQueryGetPlayerCumulative'

interface PlayerCumulativePanelProps {
	playerId: string
}

// Format currency
function formatCurrency(amount: number): string {
	return new Intl.NumberFormat('th-TH', {
		style: 'currency',
		currency: 'THB',
		minimumFractionDigits: 0,
		maximumFractionDigits: 2,
	}).format(amount)
}

// Stat item component for cumulative
function CumulativeStatItem({
	icon,
	label,
	value,
	count,
	colorClass,
}: {
	icon: React.ReactNode
	label: string
	value: string
	count?: string
	colorClass?: string
}) {
	return (
		<div className="flex items-start gap-3 rounded-md px-3 py-2.5">
			<div className={cn('mt-0.5', colorClass)}>{icon}</div>
			<div className="min-w-0 flex-1">
				<span className="text-sm text-muted-foreground">{label}</span>
				<div className="text-base font-semibold">{value}</div>
				{count && <div className="text-xs text-muted-foreground">{count}</div>}
			</div>
		</div>
	)
}

// Loading skeleton
function CumulativeSkeleton() {
	return (
		<div className="space-y-2 p-2">
			{Array.from({ length: 4 }).map((_, i) => (
				<div key={i} className="flex items-start gap-2 p-2">
					<Skeleton className="h-4 w-4 rounded" />
					<div className="flex-1 space-y-1">
						<Skeleton className="h-3 w-16" />
						<Skeleton className="h-4 w-24" />
					</div>
				</div>
			))}
		</div>
	)
}

export function PlayerCumulativePanel({
	playerId,
}: PlayerCumulativePanelProps) {
	const [isOpen, setIsOpen] = useState(true)
	const {
		data: cumulativeData,
		isLoading,
		refetch,
		isFetching,
	} = useQueryGetPlayerCumulative(playerId, {
		enabled: !!playerId,
	})

	if (!playerId) {
		return null
	}

	if (isLoading) {
		return <CumulativeSkeleton />
	}

	if (!cumulativeData) {
		return null
	}

	return (
		<Collapsible open={isOpen} onOpenChange={setIsOpen}>
			<CollapsibleTrigger className="flex w-full items-center justify-between rounded-md px-3 py-2 text-base font-semibold hover:bg-accent">
				<span>สรุปข้อมูลการเงิน</span>
				<div className="flex items-center gap-1">
					<Button
						variant="ghost"
						size="icon"
						className="h-7 w-7"
						onClick={(e) => {
							e.stopPropagation()
							refetch()
						}}
						disabled={isFetching}
					>
						<RefreshCw
							className={cn('h-4 w-4', isFetching && 'animate-spin')}
						/>
					</Button>
					<ChevronDown
						className={cn(
							'h-4 w-4 transition-transform duration-200',
							isOpen && 'rotate-180',
						)}
					/>
				</div>
			</CollapsibleTrigger>
			<CollapsibleContent>
				<div className="space-y-1 py-2">
					{/* Total Deposit */}
					<CumulativeStatItem
						icon={<ArrowDownToLine className="h-5 w-5" />}
						label="ยอดฝากรวม"
						value={formatCurrency(cumulativeData.totalDepositAmount)}
						count={`จำนวน ${cumulativeData.totalDepositTime.toFixed(0)} ครั้ง`}
						colorClass="text-green-500"
					/>

					{/* Total Withdraw */}
					<CumulativeStatItem
						icon={<ArrowUpFromLine className="h-5 w-5" />}
						label="ยอดถอนรวม"
						value={formatCurrency(cumulativeData.totalWithdrawAmount)}
						count={`จำนวน ${cumulativeData.totalWithdrawTime.toFixed(0)} ครั้ง`}
						colorClass="text-red-500"
					/>

					{/* Total Turnover */}
					<CumulativeStatItem
						icon={<Trophy className="h-5 w-5" />}
						label="ยอดเทิร์นรวม"
						value={formatCurrency(cumulativeData.totalTurnoverAmount)}
						colorClass="text-yellow-500"
					/>

					{/* Total Deposit Bonus */}
					<CumulativeStatItem
						icon={<Gift className="h-5 w-5" />}
						label="โบนัสฝากรวม"
						value={formatCurrency(cumulativeData.totalDepositBonusAmount)}
						colorClass="text-purple-500"
					/>
				</div>
			</CollapsibleContent>
		</Collapsible>
	)
}
